/**
 * EditableTransparencySection - Transparency Footer with element-level editing
 * 
 * Each element (heading, items, footer text) is wrapped with SelectableElement
 * for click-to-select in the Page Builder Studio.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import SelectableElement from './SelectableElement';
import InlineTextEditor from './InlineTextEditor';
import { 
  LANDING_TRANSPARENCY_ELEMENTS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Element animation wrapper
const AnimatedElement = ({ 
  children, 
  motion,
  animationKey,
  disabled,
  className = '' 
}) => {
  const ref = useRef(null);
  const prevKeyRef = useRef(animationKey);
  
  const { durationMs = 700, delayMs = 0, easing = 'smooth', fromOpacity = 0, fromY = 30, fromScale = 1 } = motion || {};
  const easingValue = EASING_VALUES[easing] || EASING_VALUES.smooth;

  useEffect(() => {
    if (animationKey !== prevKeyRef.current && ref.current) {
      prevKeyRef.current = animationKey;
      ref.current.style.opacity = fromOpacity;
      ref.current.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
      void ref.current.offsetWidth;
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0) scale(1)';
        }
      }, 50);
    }
  }, [animationKey, fromOpacity, fromY, fromScale]);

  useEffect(() => {
    if (disabled || !ref.current) return;
    const el = ref.current;
    el.style.opacity = fromOpacity;
    el.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
    el.style.transition = `opacity ${durationMs}ms ${easingValue} ${delayMs}ms, transform ${durationMs}ms ${easingValue} ${delayMs}ms`;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, durationMs, delayMs, easingValue, fromOpacity, fromY, fromScale]);

  if (disabled) return <div className={className}>{children}</div>;
  return <div ref={ref} className={className}>{children}</div>;
};

const getTextStyleClass = (styles) => {
  const sizeMap = {
    'xs': 'text-xs', 'sm': 'text-sm', 'base': 'text-base', 'lg': 'text-lg',
    'xl': 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl',
  };
  const weightMap = {
    'light': 'font-light', 'normal': 'font-normal', 'medium': 'font-medium',
    'semibold': 'font-semibold', 'bold': 'font-bold',
  };
  const alignMap = { 'left': 'text-left', 'center': 'text-center', 'right': 'text-right' };
  
  return `${sizeMap[styles?.size] || 'text-base'} ${weightMap[styles?.weight] || ''} ${alignMap[styles?.alignment] || 'text-center'}`;
};

const EditableTransparencySection = ({ 
  props = {},
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  animationKey = 0,
  disabled = false,
}) => {
  const elements = useMemo(() => {
    const map = {};
    LANDING_TRANSPARENCY_ELEMENTS.forEach(el => { map[el.id] = el; });
    return map;
  }, []);

  const getValue = (elementId) => {
    const element = elements[elementId];
    if (!element) return '';
    return getElementValue(props, element);
  };

  const getStyles = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementStyles(props, element);
  };

  const getMotion = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementMotion(props, element);
  };

  const handleSelectElement = (elementId) => onSelectElement?.(elementId);
  const handleDoubleClick = (elementId) => onStartEditing?.(elementId);
  const handleSaveText = (elementId, newValue) => {
    onUpdateElementValue?.(elementId, newValue);
    onStopEditing?.();
  };

  // Safe string coercion - ensures we never render objects as React children
  const safeString = (val, fallback = '') => {
    if (!val) return fallback;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      if (val.title) return val.title;
      if (val.label) return val.label;
      if (val.text) return val.text;
    }
    return fallback;
  };

  // Heading
  const headingValue = safeString(getValue('transparency.heading'), 'What "Early Access" Means');
  const headingStyles = getStyles('transparency.heading');
  const headingMotion = getMotion('transparency.heading');

  // Footer text
  const footerValue = safeString(getValue('transparency.footer'), "You're helping us build this the right way.");
  const footerStyles = getStyles('transparency.footer');
  const footerMotion = getMotion('transparency.footer');

  // Default items
  const defaultItems = [
    'Some features are still being refined',
    'You may see placeholder or test accounts',
    'The platform will visibly evolve'
  ];

  const items = props.items || defaultItems;

  return (
    <section className="py-16 bg-zinc-950 border-t border-zinc-800" data-testid="transparency-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        {/* Heading */}
        <AnimatedElement
          motion={headingMotion}
          animationKey={animationKey}
          disabled={disabled}
        >
          <SelectableElement
            elementId="transparency.heading"
            elementLabel="Section Heading"
            elementType="text"
            isSelected={selectedElementId === 'transparency.heading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <h3 
              className={`text-white mb-6 ${getTextStyleClass(headingStyles)}`}
              style={{ opacity: headingStyles.opacity ?? 1 }}
              onDoubleClick={() => handleDoubleClick('transparency.heading')}
            >
              {editingElementId === 'transparency.heading' ? (
                <InlineTextEditor
                  value={headingValue}
                  isEditing={true}
                  maxLength={60}
                  onSave={(val) => handleSaveText('transparency.heading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                headingValue
              )}
            </h3>
          </SelectableElement>
        </AnimatedElement>
        
        {/* Transparency Items */}
        <AnimatedElement
          motion={{ ...headingMotion, delayMs: (headingMotion.delayMs || 0) + 150 }}
          animationKey={animationKey}
          disabled={disabled}
          className="space-y-3 mb-6"
        >
          {items.map((item, index) => {
            const elementId = `transparency.item_${index}`;
            const itemStyles = getStyles(elementId);
            const rawValue = getValue(elementId);
            const itemValue = safeString(rawValue, typeof item === 'string' ? item : '');
            
            return (
              <SelectableElement
                key={`transparency-item-${index}`}
                elementId={elementId}
                elementLabel={`Item ${index + 1}`}
                elementType="text"
                isSelected={selectedElementId === elementId}
                onSelect={handleSelectElement}
                disabled={disabled}
              >
                <div 
                  className="flex items-center justify-center gap-2 text-zinc-400"
                  style={{ opacity: itemStyles.opacity ?? 0.7 }}
                  onDoubleClick={() => handleDoubleClick(elementId)}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500/50" />
                  {editingElementId === elementId ? (
                    <InlineTextEditor
                      value={itemValue}
                      isEditing={true}
                      maxLength={100}
                      onSave={(val) => handleSaveText(elementId, val)}
                      onCancel={onStopEditing}
                      className="inline"
                    />
                  ) : (
                    <span>{itemValue}</span>
                  )}
                </div>
              </SelectableElement>
            );
          })}
        </AnimatedElement>
        
        {/* Footer Text */}
        <AnimatedElement
          motion={footerMotion}
          animationKey={animationKey}
          disabled={disabled}
        >
          <SelectableElement
            elementId="transparency.footer"
            elementLabel="Footer Text"
            elementType="text"
            isSelected={selectedElementId === 'transparency.footer'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <p 
              className={`text-zinc-500 italic ${getTextStyleClass(footerStyles)}`}
              style={{ opacity: footerStyles.opacity ?? 0.5 }}
              onDoubleClick={() => handleDoubleClick('transparency.footer')}
            >
              {editingElementId === 'transparency.footer' ? (
                <InlineTextEditor
                  value={footerValue}
                  isEditing={true}
                  maxLength={100}
                  onSave={(val) => handleSaveText('transparency.footer', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                footerValue
              )}
            </p>
          </SelectableElement>
        </AnimatedElement>
      </div>
    </section>
  );
};

export default EditableTransparencySection;
